// client/src/services/poaContractors.ts
// API client for the POA contractor register — stored details for Form 2848
// representatives who are not BMSS employees.

export interface POAContractor {
  id:                    string;
  staffUser:             string;
  fullName:              string;
  address:               string;
  phone:                 string;
  fax:                   string;
  cafNumber:             string;
  ptin:                  string;
  designationCode:       string;
  licensingJurisdiction: string;
  licenseNumber:         string;
  notes:                 string;
  isActive:              boolean;
  createdAt?:            string;
  createdBy?:            string | null;
  updatedAt?:            string | null;
  updatedBy?:            string | null;
}

/** Everything except the server-assigned audit fields. `id` absent = create. */
export type POAContractorInput = Omit<
  POAContractor, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
> & { id?: string };

async function unwrap(res: Response) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function listContractors(): Promise<POAContractor[]> {
  const data = await unwrap(await fetch('/api/poa-contractors', { credentials: 'include' }));
  return data.contractors ?? [];
}

export async function saveContractor(input: POAContractorInput): Promise<{ id: string }> {
  return unwrap(await fetch('/api/poa-contractors', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  }));
}

/** Deactivates rather than deletes — prior POAs were prefilled from this record. */
export async function deactivateContractor(id: string): Promise<void> {
  await unwrap(await fetch(`/api/poa-contractors?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  }));
}
